import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  useGetApiCompaniesCurrentServiceconnections,
  usePatchApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { PersonalDocumentsServiceModuleModel, ServiceModuleModel } from '@/api/model'
import { notify } from '@/lib/notify'
import { Label } from '@/components/shadcn/label'
import { Button } from '@/components/shadcn/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { AutoTranslateAllButton } from '@/components/common/translations/AutoTranslateAllButton'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

/**
 * Editor for PersonalDocuments modules. Specific: the service connection the
 * document import pulls from — selectable or removable, since a module without
 * one simply has no import (the button on the page is then gone).
 */
export function EditPersonalDocumentsServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-personal-documents', 'service-modules'])
  const standardFields = useStandardServiceModuleFields(module)
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const queryClient = useQueryClient()

  const detail = module as PersonalDocumentsServiceModuleModel

  // The endpoint returns every connection of the company at once — no paging.
  const { data: connectionsData, isLoading: connectionsLoading } =
    useGetApiCompaniesCurrentServiceconnections()
  const connections = connectionsData?.data ?? []

  const patch = usePatchApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleId()

  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(
    detail.serviceConnectionId ?? null,
  )
  useEffect(() => {
    setSelectedConnectionId(detail.serviceConnectionId ?? null)
  }, [detail.serviceConnectionId])

  // A stored connection stays selectable even when the list doesn't offer it —
  // the list needs `ServiceConnections.View`, which editing a module does not.
  // Without this the field would look empty although an assignment exists.
  const options =
    selectedConnectionId != null && !connections.some((c) => c.id === selectedConnectionId)
      ? [...connections, { id: selectedConnectionId, name: null }]
      : connections

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: {
          ...standardFields.buildBasePayload(),
          serviceConnectionId: selectedConnectionId,
        },
      })
      await standardFields.saveTranslations()
      standardFields.triggerTranslation(String(module.id), autoTranslateAll)
      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
      })
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-2">
      <Label>{t('personalDocuments.edit.connectionLabel')}</Label>
      <div className="flex gap-2">
        <Select
          value={selectedConnectionId?.toString() ?? 'none'}
          onValueChange={(value) =>
            setSelectedConnectionId(value === 'none' ? null : Number(value))
          }
          disabled={connectionsLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t('personalDocuments.edit.connectionPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('personalDocuments.edit.noConnection')}</SelectItem>
            {options.map((connection) => (
              <SelectItem key={connection.id} value={connection.id.toString()}>
                {connection.name ?? t('personalDocuments.edit.connectionFallback', { id: connection.id })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedConnectionId != null && (
          <Button variant="outline" size="icon" onClick={() => setSelectedConnectionId(null)}>
            <X className="size-4 text-destructive" />
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {t('personalDocuments.edit.connectionHint')}
      </p>
    </div>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('personalDocuments.edit.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={standardFields} moduleType={module.type} autoTranslateAll={autoTranslateAll} />}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending}
      autoTranslateButton={
        <AutoTranslateAllButton
          properties={standardFields.autoTranslateProperties}
          autoTranslateAll={autoTranslateAll}
          onAutoTranslateAllChange={setAutoTranslateAll}
        />
      }
    />
  )
}
